/**
 * isProjectCompleted — canonical "is this project completed?" predicate.
 *
 * Background: prior to DEV-851, two callsites (App.tsx:1673-1686 and
 * DataReliabilityTab.tsx:267-279) inlined the same logic with subtly
 * different fallback chains. This util is the single source of truth.
 *
 * Semantics: a project is "completed" when BOTH:
 *   1. Its project_status is "Completed" (case-insensitive, trimmed), AND
 *   2. Its end date is non-empty AND on-or-before today (ISO date compare).
 *
 * If either condition fails, the project is NOT completed — including
 * the case where status says "Completed" but end_date is missing or
 * still in the future (e.g., scheduled future closure).
 *
 * Field-name fallback chains accommodate the historical naming drift in
 * the codebase: end_date has been called `finish_date`, `project_end_date`,
 * and `end_date` at various points. The fallback order matches App.tsx's
 * established precedence so behavior is preserved.
 *
 * NOTE: This is a UI-semantics predicate (drives Complete-state rendering).
 * It is intentionally distinct from the new data-reliability engine's
 * `isNowApplicable()` lifecycle gate, which also returns false for
 * not-yet-started projects. Don't conflate the two.
 */

import type { ProjectConfigOverride } from './projectConfigOverrides';

/**
 * Permissive shape — the "project" argument may be the row itself,
 * the row's nested `.project` object, or some hybrid. We check both
 * levels of nesting and multiple legacy end-date field names.
 */
export interface ProjectLikeForCompletion {
  project_status?: string | null;
  finish_date?: string | null;
  project_end_date?: string | null;
  end_date?: string | null;
  project?: {
    project_status?: string | null;
    finish_date?: string | null;
    project_end_date?: string | null;
    end_date?: string | null;
  } | null;
}

export function isProjectCompleted(
  project: ProjectLikeForCompletion | null | undefined,
  override: ProjectConfigOverride | null | undefined,
  todayIso: string,
): boolean {
  // ── Status check (case-insensitive, trimmed) ─────────────────────
  const statusRaw =
    override?.project_status
    ?? project?.project_status
    ?? project?.project?.project_status
    ?? '';
  const status = String(statusRaw).trim().toUpperCase();
  if (status !== 'COMPLETED') return false;

  // ── End-date check ──────────────────────────────────────────────
  // Fallback chain matches App.tsx's established precedence:
  // override → flat finish_date → flat project_end_date → flat end_date
  // → nested .project.finish_date → .project.project_end_date → .project.end_date
  const endDateRaw =
    override?.project_end_date
    ?? project?.finish_date
    ?? project?.project_end_date
    ?? project?.end_date
    ?? project?.project?.finish_date
    ?? project?.project?.project_end_date
    ?? project?.project?.end_date
    ?? '';
  const endDate = String(endDateRaw).trim();
  if (!endDate) return false;

  // ISO date string comparison is lexicographic-correct for YYYY-MM-DD.
  return endDate <= todayIso;
}
